import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * BalanceHeaderPanel
 *
 * Left-docked slide-in panel used as the header drawer on mobile and the
 * main navigation overlay on desktop. It's triggered by elements with
 * `[data-header-panel-toggle]` (mobile hamburger) or
 * `[data-header-panel-gender-open]` (desktop Women/Men bar buttons), and
 * closed by elements with `[data-header-panel-close]` or by pressing Escape.
 *
 * The component also hooks desktop two-column linklists: hovering a primary
 * link with `data-panel-target` activates the matching secondary list.
 */
const PANEL_OPEN_CLASS_BODY = 'header-panel-open';

class BalanceHeaderPanel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  /** @type {any} */
  #designerSwiper = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('mouseover', this.#onPrimaryHover);

    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeyDown);
    document.addEventListener('balance:gender-change', this.#onGenderChange);

    this.#initDesignerSwiper();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('mouseover', this.#onPrimaryHover);

    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeyDown);
    document.removeEventListener('balance:gender-change', this.#onGenderChange);

    if (this.#designerSwiper && typeof this.#designerSwiper.destroy === 'function') {
      try {
        this.#designerSwiper.destroy(true, true);
      } catch (e) {
        // ignore
      }
      this.#designerSwiper = null;
    }

    if (this.isOpen) this.#releaseBody();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    this.#lockBody();
    this.#syncTriggerState(true);
    this.#activateDefaultSubpanel();
    this.#refreshDesignerSwiper();

    const aside = this.querySelector('.header-panel__aside');
    if (aside instanceof HTMLElement) {
      requestAnimationFrame(() => trapFocus(aside));
    }
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.#releaseBody();
    this.#syncTriggerState(false);
    removeTrapFocus();

    if (this.#lastTrigger instanceof HTMLElement) {
      this.#lastTrigger.focus({ preventScroll: true });
    }
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  #lockBody = () => {
    document.body.classList.add(PANEL_OPEN_CLASS_BODY);
    document.body.style.overflow = 'hidden';
  };

  #releaseBody = () => {
    document.body.classList.remove(PANEL_OPEN_CLASS_BODY);
    document.body.style.overflow = '';
  };

  #syncTriggerState = (expanded) => {
    const sel =
      '[data-header-panel-toggle][aria-controls="' +
      this.id +
      '"], [data-header-panel-gender-open][aria-controls="' +
      this.id +
      '"]';
    document.querySelectorAll(sel).forEach((t) => {
      t.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  };

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-header-panel-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const backBtn = target.closest('[data-subpanel-back]');
    if (backBtn instanceof HTMLElement) {
      event.preventDefault();
      this.#deactivateSubpanels();
      return;
    }

    const openBtn = target.closest('[data-subpanel-target]');
    if (openBtn instanceof HTMLElement) {
      const id = openBtn.dataset.subpanelTarget;
      if (!id) return;
      event.preventDefault();
      const slide = !this.#isDesktop();
      this.activateSubpanel(id, { slide });
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      // Close an open mobile subpanel first, otherwise close the panel.
      const activeMobileSubpanel = this.querySelector(
        '.header-panel__subpanel[aria-hidden="false"]'
      );
      if (activeMobileSubpanel instanceof HTMLElement) {
        this.#deactivateSubpanels();
        return;
      }
      this.close();
    }
  };

  /** @param {MouseEvent} event */
  #onPrimaryHover = (event) => {
    if (!this.#isDesktop()) return;
    const btn = event.target instanceof Element ? event.target.closest('[data-subpanel-target]') : null;
    if (!(btn instanceof HTMLElement)) return;
    const id = btn.dataset.subpanelTarget;
    if (!id) return;
    this.activateSubpanel(id);
  };

  #isDesktop = () => {
    try {
      return window.matchMedia('(min-width: 750px)').matches;
    } catch (e) {
      return false;
    }
  };

  /**
   * Activate a subpanel by id. On desktop this swaps the right-hand column;
   * on mobile pass `{ slide: true }` to slide the subpanel in from the right.
   * @param {string} id
   * @param {{ slide?: boolean }} [options]
   */
  activateSubpanel = (id, options = {}) => {
    const { slide = false } = options;
    this.querySelectorAll('.header-panel__subpanel').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const active = el.id === id;
      el.classList.toggle('is-active', active);
      if (slide) {
        el.setAttribute('aria-hidden', active ? 'false' : 'true');
      } else {
        el.setAttribute('aria-hidden', 'true');
      }
    });
    this.querySelectorAll('.header-panel__menu-item--expandable').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const btn = el.querySelector('[data-subpanel-target]');
      const target = btn instanceof HTMLElement ? btn.dataset.subpanelTarget : null;
      el.classList.toggle('is-active', target === id);
    });
  };

  #deactivateSubpanels = () => {
    this.querySelectorAll('.header-panel__subpanel').forEach((el) => {
      el.setAttribute('aria-hidden', 'true');
    });
  };

  #activateDefaultSubpanel = () => {
    const first = this.querySelector('.header-panel__menu-item--expandable [data-subpanel-target]');
    if (!(first instanceof HTMLElement)) return;
    const id = first.dataset.subpanelTarget;
    if (!id) return;
    this.activateSubpanel(id);
  };

  /**
   * Initialize the designer rail as a Swiper. If `swiper.min.js` is still
   * deferred when the module resolves we retry after `window` `load`.
   */
  #initDesignerSwiper = () => {
    if (this.#designerSwiper) return;
    const el = this.querySelector('[data-header-panel-designer-swiper]');
    if (!(el instanceof HTMLElement)) return;

    if (typeof window.Swiper !== 'function') {
      window.addEventListener('load', this.#initDesignerSwiper, { once: true });
      return;
    }

    try {
      this.#designerSwiper = new window.Swiper(el, {
        slidesPerView: 'auto',
        spaceBetween: 14,
        watchOverflow: true,
        observer: true,
        observeParents: true,
      });
    } catch (e) {
      this.#designerSwiper = null;
    }
  };

  /**
   * Ask Swiper to re-measure its slides. Deferred to the next frame so the
   * panel's open animation and any body-class flip settle first.
   */
  #refreshDesignerSwiper = () => {
    if (!this.#designerSwiper) return;
    requestAnimationFrame(() => {
      if (!this.#designerSwiper) return;
      try {
        this.#designerSwiper.update();
      } catch (e) {
        // ignore
      }
    });
  };

  #onGenderChange = () => {
    this.#refreshDesignerSwiper();
  };

  /**
   * Capture trigger clicks anywhere on the page so the panel knows which
   * element opened it (used to restore focus after close).
   * @param {Event} event
   */
  #onDocumentClick = (event) => {
    const genderTrigger =
      event.target instanceof Element ? event.target.closest('[data-header-panel-gender-open]') : null;
    if (genderTrigger instanceof HTMLElement) {
      const panelId = genderTrigger.getAttribute('aria-controls');
      if (panelId && panelId !== this.id) return;

      const gender = genderTrigger.dataset.headerPanelGenderOpen;
      if (gender !== 'women' && gender !== 'men') return;

      event.preventDefault();
      this.#lastTrigger = genderTrigger;

      const tabs = this.querySelector('balance-gender-tabs');
      if (tabs && typeof tabs.setGenderExplicit === 'function') {
        tabs.setGenderExplicit(gender);
      }

      if (!this.isOpen) this.open();
      return;
    }

    const target =
      event.target instanceof Element ? event.target.closest('[data-header-panel-toggle]') : null;
    if (!(target instanceof HTMLElement)) return;

    const targetId = target.getAttribute('aria-controls');
    if (targetId && targetId !== this.id) return;

    event.preventDefault();
    this.#lastTrigger = target;
    this.toggle();
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };
}

if (!customElements.get('balance-header-panel')) {
  customElements.define('balance-header-panel', BalanceHeaderPanel);
}

/**
 * Gender tab selector used at the top of the header panel.
 *
 * Toggles one of `display-men-only` / `display-women-only` /
 * `display-neutral-only` on `<body>`, persisting the selection in
 * localStorage under `balance:gender` so it survives across navigations.
 */
const GENDER_STORAGE_KEY = 'balance:gender';
const GENDER_BODY_CLASSES = ['display-men-only', 'display-women-only', 'display-neutral-only'];
const VALID_GENDERS = ['men', 'women', 'neutral'];
const GENDER_CHANGE_EVENT = 'balance:gender-change';

/**
 * @param {string | null} value
 * @returns {'men' | 'women' | 'neutral'}
 */
function normalizeGender(value) {
  return VALID_GENDERS.includes(/** @type {any} */ (value)) ? /** @type {any} */ (value) : 'neutral';
}

/**
 * @param {'men' | 'women' | 'neutral'} gender
 */
function applyGenderToBody(gender) {
  const body = document.body;
  if (!body) return;
  body.classList.remove(...GENDER_BODY_CLASSES);
  body.classList.add(`display-${gender}-only`);
}

/**
 * Apply the gender class and broadcast a `balance:gender-change` event so
 * components that were previously `display: none` (and therefore unable to
 * measure their layout — Swiper sliders in particular) can refresh.
 *
 * @param {'men' | 'women' | 'neutral'} gender
 */
function setGender(gender) {
  applyGenderToBody(gender);
  try {
    document.dispatchEvent(
      new CustomEvent(GENDER_CHANGE_EVENT, { detail: { gender } })
    );
  } catch (e) {
    // older browsers or test environments without CustomEvent — ignore
  }
}

class BalanceGenderTabs extends HTMLElement {
  connectedCallback() {
    this.#syncFromStorage();
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #syncFromStorage = () => {
    let stored = null;
    try {
      stored = localStorage.getItem(GENDER_STORAGE_KEY);
    } catch (e) {
      stored = null;
    }
    const gender = normalizeGender(stored);
    applyGenderToBody(gender);
    this.#setActiveTab(gender);
  };

  /**
   * @param {'men' | 'women' | 'neutral'} gender
   */
  #setActiveTab = (gender) => {
    const buttons = this.querySelectorAll('button[data-gender]');
    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const isActive = button.dataset.gender === gender;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  };

  /**
   * @param {Event} event
   */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button[data-gender]') : null;
    if (!(target instanceof HTMLButtonElement)) return;

    const next = normalizeGender(target.dataset.gender ?? null);
    const current = normalizeGender(this.#readStored());
    const gender = current === next ? 'neutral' : next;

    setGender(gender);
    this.#setActiveTab(gender);
    this.#writeStored(gender);
  };

  #readStored = () => {
    try {
      return localStorage.getItem(GENDER_STORAGE_KEY);
    } catch (e) {
      return null;
    }
  };

  /**
   * @param {'men' | 'women' | 'neutral'} gender
   */
  #writeStored = (gender) => {
    try {
      localStorage.setItem(GENDER_STORAGE_KEY, gender);
    } catch (e) {
      // ignore storage errors (private mode, quota, etc.)
    }
  };

  /**
   * Sets women/men from desktop header triggers. Does not toggle to neutral.
   *
   * @param {'women' | 'men'} gender
   */
  setGenderExplicit(gender) {
    if (gender !== 'women' && gender !== 'men') return;
    setGender(gender);
    this.#setActiveTab(gender);
    this.#writeStored(gender);
  }
}

if (!customElements.get('balance-gender-tabs')) {
  customElements.define('balance-gender-tabs', BalanceGenderTabs);
}
